import React, { useState } from 'react';
import { useAuth } from '../lib/Auth';

/**
 * "Who's watching?" — the picker between signing in and the app.
 *
 * Profiles are people inside one account, so this is not a second gate: getting
 * here means the password was already right, and picking a name is a choice
 * about whose continue-watching and whose list to show, not an authorisation.
 * Which is why there is no password on a tile and no wrong answer.
 *
 * It is only ever shown for an account with more than one profile. A household
 * of one is never asked.
 */

/**
 * One or two letters for the tile.
 *
 * Two words give their initials ("Anna Marie" → AM); one word gives its first
 * letter only, because a single name's second letter says nothing and reads as
 * a typo at this size.
 */
const initials = (name) => {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const Profiles = () => {
  const { user, chooseProfile, signOut } = useAuth();
  // Which tile was pressed. Held per id rather than as one boolean so the tile
  // that was actually clicked is the one that shows it is working.
  const [choosing, setChoosing] = useState(null);
  const [error, setError] = useState('');

  const profiles = user?.profiles || [];

  const pick = async (id) => {
    if (choosing) return;
    setChoosing(id);
    setError('');
    try {
      // Resolves into a page load, so there is no success path to handle here
      await chooseProfile(id);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not switch to that profile.');
      setChoosing(null);
    }
  };

  return (
    <div className="who">
      <div className="who-inner">
        <h1 className="who-title">Who&rsquo;s watching?</h1>

        <div className="who-grid">
          {profiles.map((profile) => (
            <button
              key={profile.id}
              type="button"
              className={`who-tile${choosing === profile.id ? ' is-busy' : ''}`}
              onClick={() => pick(profile.id)}
              disabled={Boolean(choosing)}
            >
              <span className="who-face">{initials(profile.name)}</span>
              <span className="who-name">{profile.name}</span>
            </button>
          ))}
        </div>

        {error && <p className="who-alert">{error}</p>}

        <div className="who-foot">
          <span className="who-account">{user?.username}</span>
          <button type="button" className="who-signout" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profiles;
